import type { Event, Question, Section, QuestionOption } from '../types';

const API_BASE = process.env.REACT_APP_API_URL || '';

// Get auth token from localStorage
function getAuthToken(): string | null {
  return localStorage.getItem('admin_token');
}

// Set auth token in localStorage
function setAuthToken(token: string): void {
  localStorage.setItem('admin_token', token);
}

// Remove auth token
function removeAuthToken(): void {
  localStorage.removeItem('admin_token');
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = getAuthToken();
  const url = `${API_BASE}${path}`;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(url, {
    ...options,
    headers,
  });

  if (!res.ok) {
    if (res.status === 401) {
      removeAuthToken();
      throw new Error('Authentication required');
    }
    const err = await res.json().catch(() => ({}));
    throw new Error((err as { message?: string }).message || `HTTP ${res.status}`);
  }
  return res.json();
}

// Auth
export async function adminLogin(password: string): Promise<string> {
  const json = await request<{ success: boolean; token: string }>('/api/admin/login', {
    method: 'POST',
    body: JSON.stringify({ password }),
  });
  if (!json.success) throw new Error('Login failed');
  setAuthToken(json.token);
  return json.token;
}

export async function adminLogout(): Promise<void> {
  const token = getAuthToken();
  if (token) {
    try {
      await request('/api/admin/logout', { method: 'POST' });
    } catch (e) {
      // Ignore errors on logout
    }
  }
  removeAuthToken();
}

export async function adminVerify(): Promise<boolean> {
  try {
    const json = await request<{ success: boolean; authenticated: boolean }>('/api/admin/verify');
    return json.authenticated === true;
  } catch {
    return false;
  }
}

export function isAuthenticated(): boolean {
  return getAuthToken() !== null;
}

// Events
export async function getAdminEvents(): Promise<Event[]> {
  const json = await request<{ success: boolean; data: Event[] }>('/api/admin/events');
  return json.data;
}

export interface EventWithDetails {
  event: Event;
  sections: Section[];
  questions: Question[];
}

export async function getAdminEvent(id: number): Promise<EventWithDetails> {
  const json = await request<{ success: boolean; data: EventWithDetails }>(`/api/admin/events/${id}`);
  return json.data;
}

export async function createEvent(event: {
  name: string;
  slug: string;
  description?: string;
  event_date: string;
  duration_minutes?: number;
  is_active?: number;
}): Promise<Event> {
  const json = await request<{ success: boolean; data: Event }>('/api/admin/events', {
    method: 'POST',
    body: JSON.stringify(event),
  });
  return json.data;
}

export async function updateEvent(
  id: number,
  updates: {
    name?: string;
    slug?: string;
    description?: string;
    event_date?: string;
    duration_minutes?: number;
    is_active?: number;
  }
): Promise<Event> {
  const json = await request<{ success: boolean; data: Event }>(`/api/admin/events/${id}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
  return json.data;
}

export async function deleteEvent(id: number): Promise<void> {
  await request(`/api/admin/events/${id}`, { method: 'DELETE' });
}

// Sections
export async function createSection(section: {
  event_id?: number;
  name: string;
  sort_order?: number;
}): Promise<Section> {
  const json = await request<{ success: boolean; data: Section }>('/api/admin/sections', {
    method: 'POST',
    body: JSON.stringify(section),
  });
  return json.data;
}

export async function updateSection(
  id: number,
  updates: { name?: string; sort_order?: number }
): Promise<Section> {
  const json = await request<{ success: boolean; data: Section }>(`/api/admin/sections/${id}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
  return json.data;
}

export async function deleteSection(id: number): Promise<void> {
  await request(`/api/admin/sections/${id}`, { method: 'DELETE' });
}

// Questions
export interface CreateQuestionData {
  event_id: number;
  section_id?: number;
  question_type: 'STAR_RATING' | 'CHOOSE_MULTIPLE' | 'TEXT';
  question_text: string;
  sort_order?: number;
  min_value?: number;
  max_value?: number;
  step_value?: number;
  options?: Array<{ value: string; label: string; sort_order?: number }>;
}

export async function createQuestion(question: CreateQuestionData): Promise<Question> {
  const json = await request<{ success: boolean; data: Question }>('/api/admin/questions', {
    method: 'POST',
    body: JSON.stringify(question),
  });
  return json.data;
}

export interface UpdateQuestionData {
  section_id?: number;
  question_type?: 'STAR_RATING' | 'CHOOSE_MULTIPLE' | 'TEXT';
  question_text?: string;
  sort_order?: number;
  min_value?: number;
  max_value?: number;
  step_value?: number;
  options?: Array<{ value: string; label: string; sort_order?: number }>;
}

export async function updateQuestion(id: number, updates: UpdateQuestionData): Promise<Question> {
  const json = await request<{ success: boolean; data: Question }>(`/api/admin/questions/${id}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
  return json.data;
}

export async function deleteQuestion(id: number): Promise<void> {
  await request(`/api/admin/questions/${id}`, { method: 'DELETE' });
}

// Reports
export interface ReportStats {
  total_sessions: number;
  completed_sessions: number;
  in_progress_sessions: number;
  participant_count: number;
}

export interface QuestionStat {
  question_id: number;
  question_text: string;
  question_type: string;
  total_responses: number;
  average?: number;
  min?: number;
  max?: number;
  distribution?: Record<string, number>;
  option_counts?: Record<number, number>;
  option_labels?: Record<number, string>;
  text_responses?: string[];
}

export interface ReportData {
  event: Event;
  sections: Section[];
  questions: Question[];
  stats: ReportStats;
  question_stats: QuestionStat[];
  raw_responses: any[];
}

export async function getReport(eventId: number): Promise<ReportData> {
  const json = await request<{ success: boolean; data: ReportData }>(`/api/admin/reports/${eventId}`);
  return json.data;
}
